import React, { useMemo, useState, useEffect } from 'react';
import { Idol } from 'types';
import { formatModDescription } from 'data/mods';
import { ContentType, Mod } from 'types/mods';
import { getTextFromModId } from 'utils/mod-utils';
import { aggregateStatsByTradeId } from 'utils';

interface StatsSummaryProps {
  stats: Record<string, number>;
  placedItems: Idol[];
}

const StatsSummary: React.FC<StatsSummaryProps> = ({ stats, placedItems }) => {
  // State for pinned content types (multiple)
  const [pinnedContentTypes, setPinnedContentTypes] = useState<string[]>([]);
  
  // Load pinned content types from localStorage on component mount
  useEffect(() => {
    const savedPinnedTypes = localStorage.getItem('pinnedContentTypes');
    if (savedPinnedTypes) {
      try {
        const parsedTypes = JSON.parse(savedPinnedTypes);
        if (Array.isArray(parsedTypes)) {
          setPinnedContentTypes(parsedTypes);
        }
      } catch (e) {
        console.error("Error parsing pinned content types:", e);
        localStorage.removeItem('pinnedContentTypes');
      }
    }
  }, []);
  
  // Save pinned content types to localStorage whenever they change
  useEffect(() => {
    if (pinnedContentTypes.length > 0) {
      localStorage.setItem('pinnedContentTypes', JSON.stringify(pinnedContentTypes));
    } else {
      localStorage.removeItem('pinnedContentTypes');
    }
  }, [pinnedContentTypes]);
  
  // Handle pinning/unpinning a content type
  const handlePinToggle = (contentType: string) => {
    if (pinnedContentTypes.includes(contentType)) {
      // Remove from pinned types if already pinned
      setPinnedContentTypes(prev => prev.filter(type => type !== contentType));
    } else {
      // Add to pinned types
      setPinnedContentTypes(prev => [...prev, contentType]);
    }
  };

  // Aggregate stats by tradeId
  const aggregatedStats = useMemo(() => {
    return aggregateStatsByTradeId(stats, placedItems);
  }, [stats, placedItems]);

  // Calculate idol counts for each aggregated stat (by tradeId)
  const idolCountsByTradeId = useMemo(() => {
    const counts: Record<string, number> = {};
    
    aggregatedStats.forEach(({ tradeId, modIds }) => {
      // Count how many idols have any of these modIds
      const idolsWithThisStat = new Set<string>();
      placedItems.forEach(item => {
        item?.stats?.forEach(stat => {
          if (modIds.includes(stat.modId)) {
            idolsWithThisStat.add(item.id);
          }
        });
      });
      counts[tradeId] = idolsWithThisStat.size;
    });
    
    return counts;
  }, [aggregatedStats, placedItems]);
  
  // Group stats by contentType
  const statsByContentType = useMemo(() => {
    const grouped: Record<ContentType | string, { contentType: string; explicitType: string; displayText: string; idolCount: number }[]> = {};
    
    // Initialize with "misc" for unknown content types
    grouped[ContentType.MISC] = [];
    
    aggregatedStats.forEach(({ tradeId, value, modIds }) => {
      // Use the first modId to get the stat information
      const firstModId = modIds[0];
      
      // Find any placed item that has a stat with this modId
      const itemWithStat = placedItems.find(item => 
        item?.stats?.some(s => modIds.includes(s.modId))
      );
      
      // Get the stat from the item
      const stat = itemWithStat?.stats?.find(s => modIds.includes(s.modId));
      
      // Use the text from the stat if available
      const statTextFromItem = getTextFromModId(firstModId);
      
      // Use the text from the item if available
      const rawText = statTextFromItem || "Unknown modifier";
      
      // Format the description with proper value
      let displayText = rawText;
      
      value = Number(value.toFixed(2));

      // Try the new format first
      if (typeof tradeId === 'string' && (tradeId.startsWith('prefix_') || tradeId.startsWith('suffix_') || tradeId.startsWith('explicit.'))) {
        // Assuming we can find this in our mod database, but providing fallback if not
        const mod = {
          description: rawText,
          type: tradeId.startsWith('prefix_') ? 'prefix' as const : 'suffix' as const,
          id: tradeId as string
        };
        displayText = formatModDescription(mod as unknown as Mod, value);
      } 
      
      // ONLY replace the numeric range pattern (X–Y) with the aggregated value
      // Don't try to be smart about "an additional" or other text
      if (value > 0 && displayText) {
        displayText = displayText.replace(/\((\+?)(\d+(?:\.\d+)?)–(\d+(?:\.\d+)?)\)(%?)/g, (match, plusSign, min, max, percent) => {
          return `${plusSign}${value}${percent}`;
        });
      }
      
      // Get the contentType from the stat
      const contentType = stat?.contentType || 'Unknown';
      const explicitType = stat?.explicitType || 'Unknown';
      
      // Get the idol count for this stat
      const idolCount = idolCountsByTradeId[tradeId] || 0;
      
      // Add to the appropriate group
      if (contentType && Object.values(ContentType).includes(contentType as ContentType)) {
        if (!grouped[contentType]) {
          grouped[contentType] = [];
        }
        grouped[contentType].push({ contentType, explicitType, displayText, idolCount });
      } else {
        grouped[ContentType.MISC].push({ contentType, explicitType, displayText, idolCount });
      }
    });
    
    // Remove empty groups
    return Object.fromEntries(
      Object.entries(grouped).filter(([_, mods]) => mods.length > 0)
    );
  }, [aggregatedStats, placedItems, idolCountsByTradeId]);
  
  // Order content types: pinned first (in order they were pinned), then alphabetically
  const orderedContentTypes = useMemo(() => {
    const entries = Object.entries(statsByContentType);
    
    return entries.sort((a, b) => {
      const aIsPinned = pinnedContentTypes.includes(a[0]);
      const bIsPinned = pinnedContentTypes.includes(b[0]);
      
      // If both are pinned, maintain their order in the pinnedContentTypes array
      if (aIsPinned && bIsPinned) {
        return pinnedContentTypes.indexOf(a[0]) - pinnedContentTypes.indexOf(b[0]);
      }
      // If only a is pinned, it comes first
      if (aIsPinned) return -1;
      // If only b is pinned, it comes first
      if (bIsPinned) return 1;
      // Otherwise sort alphabetically
      return a[0].localeCompare(b[0]);
    });
  }, [statsByContentType, pinnedContentTypes]);
  
  return (
    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
      <h2 className="text-lg font-semibold mb-4">Idol Modifiers</h2>
      {Object.keys(stats).length > 0 ? (
        <div className="space-y-4">
          {orderedContentTypes.map(([contentType, mods], index) => {
            const isPinned = pinnedContentTypes.includes(contentType);
            const backgroundColor = isPinned ? 'bg-gray-600' : 'bg-gray-700'; // Use the same color for both
            return (
              <div key={`${contentType}-${index}-${mods.length}`} 
                className={`mb-4 p-3 rounded ${backgroundColor} ${isPinned ? 'border-l-4 border-blue-500 shadow-lg' : ''}`}
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-base font-medium text-blue-400 capitalize mb-2">
                    {contentType}
                  </h3>
                  <button
                    onClick={() => handlePinToggle(contentType)}
                    className={`p-1 rounded-full hover:bg-gray-600 ${isPinned ? 'text-blue-400' : 'text-gray-400'}`}
                    title={isPinned ? "Unpin this group" : "Pin this group to top"}
                  >
                    {/* Simple custom pin icon using SVG */}
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      className={isPinned ? 'transform -rotate-45' : ''}
                    >
                      <path d="M12 2v15" />
                      <path d="M5 5c0 2.5 2 3 3 4.5C9 11 9 12 7 13" />
                      <path d="M19 5c0 2.5-2 3-3 4.5-1 1.5-1 2.5 1 3.5" />
                      <path d="M12 17v5" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-2">
                  {mods.map(({ contentType, explicitType, displayText, idolCount }, modIndex) => { 
                    // Determine text color based on prefix/suffix
                    let textColorClass = "text-blue-300"; // Default
                    
                    // Check for originalId first (for new items created with updated form)
                    if (explicitType) {
                      if (explicitType.startsWith('prefix')) {
                        textColorClass = "text-[#ffa9ff]"; // Pink color for prefixes
                      } else if (explicitType.startsWith('suffix') || explicitType.includes('suffix')) {
                        textColorClass = "text-[#a9ffff]"; // Cyan color for suffixes
                      }
                    }
                    
                    // Extract the numeric value to make it bold
                    // Match patterns like "+50%", "50%", "+50", "50", etc.
                    const parts = displayText.split(/(\+?\d+(?:\.\d+)?%?)/g).filter(Boolean);
                    
                    return (
                      <div key={`${contentType}-${modIndex}`} className={`p-2.5 rounded ${backgroundColor} flex justify-between`}> 
                        <span className={textColorClass}>
                          {parts.map((part, i) => {
                            // Check if this part is a number (with optional + and %)
                            if (/^\+?\d+(?:\.\d+)?%?$/.test(part)) {
                              return <span key={i} className="font-bold">{part}</span>;
                            }
                            return <span key={i}>{part}</span>;
                          })}
                        </span>
                        {idolCount > 1 && (
                          <span className="text-gray-400 ml-2">
                            (x{idolCount})
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-400 italic">No modifiers yet. Add items to the grid to see stats.</p>
      )}
    </div>
  );
};

export default StatsSummary;